'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter, usePathname } from 'src/navigation';

import { Filters } from './_filters';
import PaginationControls from 'components/pagination';
import MediaGrid from 'components/perspectives/media-grid';
import noResultsStyles from 'components/perspectives/search/no-results.module.scss';
import { SITE_SEARCH_PER_PAGE } from 'lib/constants.mjs';
import ArticleCard from 'components/cards/article-card';
import { routeForDocument } from 'lib/routes';
import type { estypes } from '@elastic/elasticsearch';
import { epochTimeToUTCYYYYMMDD } from 'helpers/date-time';
import SearchBar from 'components/controls/search-bar';
import PageHeader from 'components/headers/page-header';
import NoResults from 'components/search/no-results';
import { perspectivesSanityType } from 'lib/search/perspectives-indices';
import type { PerspectivesElasticDocument } from './data';

type PerspectivesHit = estypes.SearchHit<PerspectivesElasticDocument>;

const PerspectivesResultCard = ({ hit }: { hit: PerspectivesHit }) => {
	const { _index, _source } = hit;
	const sanityType = perspectivesSanityType(_index);

	return (
		<ArticleCard
			contentType={sanityType}
			image={_source.cardImage}
			contributors={_source?.contributors?.map((contributor) => ({
				person: contributor,
			}))}
			date={epochTimeToUTCYYYYMMDD(_source.publicationDate)}
			link={routeForDocument({
				_type: sanityType,
				slug: { current: _source.slug },
			})}
			sideBySide={true}
			textDescription={_source.description || _source.subtitle}
			title={_source.title}
			type={'type' in _source ? _source.type : undefined}
		/>
	);
};

export const PerspectivesAll = ({
	initialResults,
	topicFilters,
	departmentFilters,
	latestArticles,
}: IPerspectivesFiltersProps) => {
	const router = useRouter();
	const pathName = usePathname();
	const searchParams = useSearchParams();
	const resultsPerPage = SITE_SEARCH_PER_PAGE;

	//state for the selected media checkboxes
	const [selectedMediaType, setSelectedMediaType] = useState(
		searchParams.get('type')?.split(',') || [],
	);

	// Add state for the selected topic
	const [selectedTopic, setSelectedTopic] = useState(
		searchParams.get('topic') || null,
	);

	// Add state for the selected department
	const [selectedDepartment, setSelectedDepartment] = useState(
		searchParams.get('department') || null,
	);

	// Add query state
	const [query, setQuery] = useState(searchParams.get('q') || null);

	//add a state to store the fetched media
	const [pageNumber, setPageNumber] = useState(
		searchParams.get('page') ? parseInt(searchParams.get('page')) : 1,
	);
	const totalCount =
		typeof initialResults.hits.total === 'number'
			? initialResults.hits.total
			: initialResults.hits.total.value;

	//when they click a media checkbox, update the selectedMediaType array
	//(which controls the checkboxes, url, and API call)
	const handleCheckboxChange = (e) => {
		const value = e.target.value;
		const isChecked = e.target.checked;

		if (isChecked) {
			setSelectedMediaType([...selectedMediaType, value]);
		} else {
			setSelectedMediaType(
				selectedMediaType.filter((media) => media !== value),
			);
		}
		setPageNumber(1);
	};

	const handleRemoveSelection = (e) => {
		const value = e.target.value;
		setSelectedMediaType(selectedMediaType.filter((media) => media !== value));
		setPageNumber(1);
	};

	const handlePaginationChange = (e) => {
		const offset = e.currentTarget.value;
		setPageNumber(pageNumber + offset / resultsPerPage);
	};

	const handleSearchChange = (e) => {
		const query = e.target.value;
		setQuery(query);
		setPageNumber(1);
	};

	//handle writing state to URL
	useEffect(() => {
		const params = new URLSearchParams(searchParams);

		selectedMediaType.length
			? params.set('type', selectedMediaType.toString())
			: params.delete('type');

		selectedTopic
			? params.set('topic', selectedTopic.toLowerCase())
			: params.delete('topic');

		selectedDepartment
			? params.set('department', selectedDepartment.toLowerCase())
			: params.delete('department');

		query ? params.set('q', query) : params.delete('q');

		pageNumber !== 1
			? params.set('page', pageNumber.toString())
			: params.delete('page');

		// only call .push on router if params are different; prevents submitting the form twice
		if (params.toString() !== searchParams.toString()) {
			router.push(`${pathName}?${params.toString()}`, {
				scroll: !!params.get('page'),
			});
		}
	}, [
		searchParams,
		selectedMediaType,
		selectedTopic,
		selectedDepartment,
		pageNumber,
		router,
		pathName,
		query,
	]);

	return (
		<main>
			<PageHeader title="All Articles, Audio, and Videos">
				<SearchBar initialQuery={query} onChange={handleSearchChange} />
			</PageHeader>
			<Filters
				selectedMediaType={selectedMediaType}
				handleCheckboxChange={handleCheckboxChange}
				topicFilters={topicFilters}
				departmentFilters={departmentFilters}
				selectedTopic={selectedTopic}
				selectedDepartment={selectedDepartment}
				handleClearAll={() => {
					setPageNumber(1);
					setSelectedDepartment(null);
					setSelectedMediaType([]);
					setSelectedTopic(null);
				}}
				handleClearTopic={() => {
					setSelectedTopic(null);
					setPageNumber(1);
				}}
				handleClearDepartment={() => {
					setSelectedDepartment(null);
					setPageNumber(1);
				}}
				handleRemoveSelection={handleRemoveSelection}
				handleTopicSelectChange={(selectedOption) => {
					setSelectedTopic(
						// @ts-expect-error TODO: fix typescript hell with react-select and prop drilling
						selectedOption ? selectedOption?.value?.toLowerCase() : null,
					);
					setPageNumber(1);
				}}
				handleDepartmentSelectChange={(selectedOption) => {
					setSelectedDepartment(
						// @ts-expect-error TODO: fix typescript hell with react-select and prop drilling
						selectedOption ? selectedOption?.value.toLowerCase() : null,
					);
					setPageNumber(1);
				}}
			/>

			{totalCount > 0 && (
				<section className="productive-component">
					<MediaGrid>
						{initialResults.hits.hits.map((hit) => (
							<PerspectivesResultCard hit={hit} key={hit._source.sanityId} />
						))}
					</MediaGrid>
				</section>
			)}

			{totalCount < 1 && (
				<div key="latestArticles">
					<section className={noResultsStyles.container}>
						<NoResults
							title={`No Results Found for ${query}`}
							body="We couldn't find any matches. Try checking your spelling, using different key words, or broadening your search."
						/>
					</section>
					<section className="productive-component">
						<h2 style={{ marginBottom: 'calc(16px + 0.5vw)' }}>
							Recently Published
						</h2>
						<MediaGrid>
							{latestArticles.hits.hits.map((hit) => (
								<PerspectivesResultCard hit={hit} key={hit._source.sanityId} />
							))}
						</MediaGrid>
					</section>
				</div>
			)}

			<PaginationControls
				offset={resultsPerPage * (pageNumber - 1)} // this api starts at page 1
				handlePaginationChange={handlePaginationChange}
				perPage={resultsPerPage}
				totalResults={totalCount}
			/>
		</main>
	);
};

interface IPerspectivesFiltersProps {
	departmentFilters?: any[];
	initialResults: estypes.SearchResponse<PerspectivesElasticDocument>;
	latestArticles?: estypes.SearchResponse<PerspectivesElasticDocument>;
	topicFilters?: any[];
}
